/**
 * Responsibility: Implement permissions behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { PrivilegeManager, MemoryPermissionStore, PermissionsMetaData, standardPermissionChecker } from 'am-i-allowed'
import type { IActor, IPrivilegeManaged } from 'am-i-allowed'
import type {
  Vessel,
  NPC,
  Companion,
  ModalAction,
  ActionCategory,
  ActionTargetRef,
  ActionToolRef,
  GameState,
  InventoryItem,
} from "@anyquest/shared-core"
import { vitalPercent } from './vitals'
import { resolveItemActionDescriptors, supportsSharedItemActionExecution } from './item-action-model'
// ============================================================
// PERMISSIONS.TS — Action gate using am-i-allowed
// Maps game state (vitals, traits, groups) to permission groups.
// Used by item-card and npc-card to determine available actions.
// ============================================================

export const pm = new PrivilegeManager(new MemoryPermissionStore())
// --- Permission groups derived from vessel state ---

function toRefId(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function itemTarget(itemName: string): ActionTargetRef {
  const label = String(itemName ?? '').trim() || 'Item'
  return {
    kind: 'item',
    id: toRefId(label) || 'item',
    label,
  }
}

function actorTarget(kind: 'companion' | 'npc', label: string): ActionTargetRef {
  const trimmed = String(label ?? '').trim() || (kind === 'companion' ? 'Companion' : 'NPC')
  return {
    kind,
    id: toRefId(trimmed) || kind,
    label: trimmed,
  }
}

function entityTool(kind: ActionToolRef['kind'], label: string): ActionToolRef {
  const trimmed = String(label ?? '').trim() || kind
  return {
    kind,
    id: toRefId(trimmed) || kind,
    label: trimmed,
  }
}

type ModalActionShapeConfig = {
  label: string
  input: string
  danger?: boolean
  topic?: string
  topicData?: unknown
  source: ModalAction['source']
  mode?: ModalAction['mode']
  category?: ActionCategory
  explicitTargets?: ActionTargetRef[]
  tool?: ActionToolRef
  tags?: string[]
}

function modalActionShape(config: ModalActionShapeConfig): ModalAction {
  return {
    label: config.label,
    input: config.input,
    ...(config.danger ? { danger: true } : {}),
    ...(config.topic ? { topic: config.topic } : {}),
    ...(config.topicData ? { topicData: config.topicData } : {}),
    source: config.source,
    mode: config.mode ?? 'do',
    ...(config.category ? { category: config.category } : {}),
    ...(config.explicitTargets?.length ? { explicitTargets: config.explicitTargets } : {}),
    ...(config.tool ? { tool: config.tool } : {}),
    ...(config.tags?.length ? { tags: config.tags } : {}),
  }
}

export function vesselGroups(vessel: Vessel): string[] {
  const groups: string[] = ['player']
  const vitality = vitalPercent(vessel.vitals, 'vitality')
  const spirit = vitalPercent(vessel.vitals, 'spirit')
  const essence = vitalPercent(vessel.vitals, 'essence')

  // Vitality tiers
  if (vitality >= 50) groups.push('healthy')
  else if (vitality >= 20) groups.push('wounded')
  else groups.push('critical')
  // Resource tiers
  if (essence >= 30) groups.push('has-essence')
  if (spirit >= 30) groups.push('has-spirit')
  // Trait-based groups (trait names become group membership)
  vessel.traits.forEach(t => groups.push(`trait:${t.name.toLowerCase().replace(/\s+/g, '-')}`))
  return groups
}

export function asActor(vessel: Vessel): IActor {
  return { id: vessel.identity, groups: vesselGroups(vessel) }
}

// --- Item entity ---

export class ItemEntity implements IPrivilegeManaged {
  readonly id: string
  readonly location: 'inventory' | 'room'
  constructor(name: string, location: 'inventory' | 'room') {
    this.id = name
    this.location = location
  }

  static readonly permissionsMetaData = new PermissionsMetaData('Item', {
    defaultUserPermissions: ['ReadCommon'],
    groupPermissions: {
      'player': ['ReadDeep', 'WriteCommon'],
    }
  })
}

// --- NPC entity ---

export class NpcEntity implements IPrivilegeManaged {
  readonly id: string
  readonly npc: NPC
  // Instance property — only healthy players can attack; wounded can trade/talk
  customPermissionChecker = async (
    privilegeManager: PrivilegeManager,
    actor: IActor,
    operation: string,
    entity: IPrivilegeManaged,
  ): Promise<boolean> => {
    const groups = (actor.groups as string[]) ?? []
    if (operation === 'EditAnything' && !groups.includes('healthy')) return false
    return standardPermissionChecker(privilegeManager, actor, operation, entity)
  }
  static readonly permissionsMetaData = new PermissionsMetaData('NPC', {
    defaultUserPermissions: ['ReadCommon'],
    groupPermissions: {
      'player': ['ReadDeep', 'WriteCommon', 'Trade'],
      'healthy': 'EditAnything'
    },
  })
  constructor(npc: NPC) {
    this.id = npc.name
    this.npc = npc
  }
}

// --- Convenience: get available item actions ---

export async function getItemActions(
  vessel: Vessel,
  itemName: string,
  location: 'inventory' | 'room',
  equipped = false,
  context: { gs?: GameState | null; item?: Partial<InventoryItem> | null } = {},
): Promise<ModalAction[]> {
  const actor = asActor(vessel)
  const entity = new ItemEntity(itemName, location)
  const canRead   = await pm.isAllowed(actor, 'ReadCommon',  entity)
  const canWrite  = await pm.isAllowed(actor, 'WriteCommon', entity)
  const actions: ModalAction[] = []
  const target = itemTarget(itemName)
  const baseTags = ['item_modal_action']
  if (canRead)  actions.push(modalActionShape({
    label: 'EXAMINE',
    input: `Examine ${itemName}`,
    source: 'item',
    category: 'examine',
    explicitTargets: [target],
    tool: entityTool('item', itemName),
    tags: [...baseTags, 'item_examine'],
  }))
  if (canWrite) {
    for (const descriptor of await resolveItemActionDescriptors(itemName, context.item, {
      location,
      equipped,
      gs: context.gs,
    })) {
      if (!supportsSharedItemActionExecution(descriptor)) continue
      actions.push(modalActionShape({
        label: descriptor.label,
        input: descriptor.input,
        source: 'item',
        mode: descriptor.mode,
        category: descriptor.category,
        explicitTargets: [target, ...(descriptor.explicitTargets ?? [])],
        tool: {
          kind: 'item',
          id: `${toRefId(itemName) || 'item'}_${toRefId(descriptor.id) || 'action'}`,
          label: `${itemName} · ${descriptor.label}`,
        },
        tags: [...baseTags, ...(descriptor.tags ?? []), `item_action_${toRefId(descriptor.id) || 'action'}`],
      }))
    }
  }
  if (canWrite) actions.push(modalActionShape({
    label: 'USE',
    input: `Use ${itemName}`,
    source: 'item',
    category: 'use',
    explicitTargets: [target],
    tool: entityTool('item', itemName),
    tags: [...baseTags, 'item_use', 'item_use_improvise'],
  }))
  if (canWrite && location === 'room') {
    actions.push(modalActionShape({
      label: 'TAKE',
      input: `Take ${itemName}`,
      source: 'item',
      category: 'take',
      explicitTargets: [target],
      tool: entityTool('item', itemName),
      tags: [...baseTags, 'item_take', 'room_item'],
    }))
  }
  if (canWrite && location === 'inventory') {
    actions.push(modalActionShape({
      label: 'DROP',
      input: `Drop ${itemName}`,
      source: 'item',
      category: 'interact',
      explicitTargets: [target],
      tool: entityTool('item', itemName),
      tags: [...baseTags, 'item_drop', 'inventory_item'],
    }))
    if (equipped) {
      actions.push(modalActionShape({
        label: 'UNEQUIP',
        input: `Unequip ${itemName}`,
        source: 'item',
        category: 'interact',
        explicitTargets: [target],
        tool: entityTool('item', itemName),
        tags: [...baseTags, 'item_unequip', 'inventory_item'],
      }))
    } else {
      actions.push(modalActionShape({
        label: 'EQUIP',
        input: `Equip ${itemName}`,
        source: 'item',
        category: 'interact',
        explicitTargets: [target],
        tool: entityTool('item', itemName),
        tags: [...baseTags, 'item_equip', 'inventory_item'],
      }))
    }
  }

  // Destruction is always dangerous — gate on write permission
  if (canWrite) actions.push(modalActionShape({
    label: 'ATTACK / DESTROY',
    danger: true,
    input: `Attack and destroy ${itemName}`,
    source: 'item',
    category: 'attack',
    explicitTargets: [target],
    tool: entityTool('attack', 'Attack'),
    tags: [...baseTags, 'item_destroy'],
  }))
  return actions
}

// --- Convenience: get companion actions (no ATTACK — party members) ---

export function getCompanionActions(vessel: Vessel, companion: Companion): ModalAction[] {
  const n = companion.name
  const target = actorTarget('companion', n)
  return [
    modalActionShape({
      label: 'TALK TO',
      input: `Talk to ${n}`,
      topic: 'ui:dialogOpen',
      topicData: { content: 'chat-dialog', data: { companionName: n }, options: { variant: 'companion', showCloseButton: true } },
      source: 'companion',
      category: 'interact',
      explicitTargets: [target],
      tool: entityTool('free-text', 'Talk'),
      tags: ['companion_modal_action', 'companion_talk'],
    }),
    modalActionShape({
      label: 'TRADE WITH',
      input: `Trade with ${n}`,
      topic: 'trade:open',
      topicData: { npc: { name: n, identity: companion.identity, lore: companion.bio }, partnerInventory: companion.inventory },
      source: 'companion',
      category: 'interact',
      explicitTargets: [target],
      tool: entityTool('free-text', 'Trade'),
      tags: ['companion_modal_action', 'companion_trade'],
    }),
    modalActionShape({
      label: 'EXAMINE',
      input: `Examine ${n} carefully — their appearance, demeanor, current state.`,
      source: 'companion',
      category: 'examine',
      explicitTargets: [target],
      tool: entityTool('free-text', 'Examine'),
      tags: ['companion_modal_action', 'companion_examine'],
    }),
    modalActionShape({
      label: 'CHECK FEELINGS',
      input: `Ask ${n} how they're feeling and what's on their mind right now.`,
      source: 'companion',
      category: 'interact',
      explicitTargets: [target],
      tool: entityTool('free-text', 'Talk'),
      tags: ['companion_modal_action', 'companion_feelings'],
    }),
  ]
}

// --- Convenience: get available NPC actions ---

export async function getNpcActions(
  vessel: Vessel,
  npc: NPC,
): Promise<ModalAction[]> {
  const actor = asActor(vessel)
  const entity = new NpcEntity(npc)
  const canRead   = await pm.isAllowed(actor, 'ReadCommon',   entity)
  const canWrite  = await pm.isAllowed(actor, 'WriteCommon',  entity)
  const canTrade  = await pm.isAllowed(actor, 'Trade',        entity)
  const canAttack = await pm.isAllowed(actor, 'EditAnything', entity)
  const actions: ModalAction[] = []
  const target = actorTarget('npc', npc.name)
  const baseTags = ['npc_modal_action']
  if (canRead)   actions.push(modalActionShape({
    label: 'EXAMINE',
    input: `Examine ${npc.name}`,
    source: 'npc',
    category: 'examine',
    explicitTargets: [target],
    tool: entityTool('free-text', 'Examine'),
    tags: [...baseTags, 'npc_examine'],
  }))
  if (canWrite)  actions.push(modalActionShape({
    label: 'TALK TO',
    input: `Talk to ${npc.name}`,
    topic: 'ui:dialogOpen',
    topicData: { content: 'talk-dialog', data: { targetName: npc.name, targetType: 'npc', prompt: `Initiate a conversation with ${npc.name}.` }, options: { showCloseButton: true } },
    source: 'npc',
    category: 'interact',
    explicitTargets: [target],
    tool: entityTool('free-text', 'Talk'),
    tags: [...baseTags, 'npc_talk'],
  }))
  if (canTrade)  actions.push(modalActionShape({
    label: 'TRADE WITH',
    input: `Trade with ${npc.name}`,
    topic: 'trade:open',
    topicData: { npc },
    source: 'npc',
    category: 'interact',
    explicitTargets: [target],
    tool: entityTool('free-text', 'Trade'),
    tags: [...baseTags, 'npc_trade'],
  }))
  if (canAttack) actions.push(modalActionShape({
    label: 'ATTACK',
    danger: true,
    input: `Attack ${npc.name}`,
    source: 'npc',
    category: 'attack',
    explicitTargets: [target],
    tool: entityTool('attack', 'Attack'),
    tags: [...baseTags, 'npc_attack'],
  }))
  return actions
}
